import { createRoot, type Root } from 'react-dom/client';
import { OverviewWidget } from './overview/OverviewWidget';
import { FloatingButton } from './floating-button/FloatingButton';
import { ChatWidget } from './chat/ChatWidget';
import type {
  CalebasseChatbotGlobal,
  ChatProps,
  FloatingButtonProps,
  OverviewProps,
} from './types/widget';
import './styles/index.css';

/**
 * Inject the Google Fonts <link> tags once per page. We split this across
 * three requests:
 *   1) Latin pack (Poppins) — always loaded, ~25 KB.
 *   2) Source Serif 4 (600) — product card titles. Its own link, so a host
 *      that already ships Poppins still gets it.
 *   3) Noto Sans SC — only when a Chinese locale mounts, ~120 KB.
 *
 * Both links use the print-media swap trick to avoid blocking first paint,
 * and rely on `display=swap` so the system fallback shows immediately.
 * Idempotent: a Poppins link already on the host page is reused.
 *
 * Hosts that prefer their own font pipeline can pre-inject these <link>s
 * (or set `data-cal-fonts="skip"` on <html>) and we will not touch them.
 */
function ensureFontsLoaded(locale?: string): void {
  if (typeof document === 'undefined') return;
  if (document.documentElement.dataset.calFonts === 'skip') return;

  const isChinese = locale === 'zh' || document.documentElement.lang?.startsWith('zh');

  // Latin (Poppins) — single family, load once per page.
  const alreadyHasLatin =
    document.querySelector('link[data-cal-fonts="latin"]') ||
    document.querySelector('link[href*="Poppins"]');
  if (!alreadyHasLatin) {
    addPreconnect('https://fonts.googleapis.com');
    addPreconnect('https://fonts.gstatic.com', true);
    addAsyncFontLink(
      'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,600;0,700;1,500&display=swap',
      'latin',
    );
  }

  if (!document.querySelector('link[data-cal-fonts="serif"], link[href*="Source+Serif+4"]')) {
    addPreconnect('https://fonts.googleapis.com');
    addPreconnect('https://fonts.gstatic.com', true);
    addAsyncFontLink(
      'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,600&display=swap',
      'serif',
    );
  }

  // Noto Sans SC — only when needed.
  if (isChinese && !document.querySelector('link[data-cal-fonts="zh"]')) {
    addAsyncFontLink(
      'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;600&display=swap',
      'zh',
    );
  }
}

function addPreconnect(href: string, crossorigin = false): void {
  if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = href;
  if (crossorigin) link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

function addAsyncFontLink(href: string, tag: string): void {
  // The print-media swap pattern: browsers don't block render-paint on
  // print stylesheets, then onload promotes it back to `all` media so
  // fonts apply normally. Falls back gracefully without JS via <noscript>.
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'print';
  link.dataset.calFonts = tag;
  link.onload = () => {
    link.media = 'all';
  };
  document.head.appendChild(link);
}

// Track mounted roots per container so unmount() knows what to tear down.
const roots = new WeakMap<HTMLElement, Root>();

function mount(el: HTMLElement, locale: string | undefined, node: JSX.Element): void {
  ensureFontsLoaded(locale);
  unmount(el);
  const root = createRoot(el);
  root.render(node);
  roots.set(el, root);
}

const mountOverview = (el: HTMLElement, props: OverviewProps): void =>
  mount(el, props.locale, <OverviewWidget {...props} />);
const mountFloatingButton = (el: HTMLElement, props: FloatingButtonProps): void =>
  mount(el, props.locale, <FloatingButton {...props} />);
const mountChat = (el: HTMLElement, props: ChatProps): void =>
  mount(el, props.locale, <ChatWidget {...props} />);

function unmount(el: HTMLElement): void {
  const root = roots.get(el);
  if (root) {
    root.unmount();
    roots.delete(el);
  }
}

const api: CalebasseChatbotGlobal = { mountOverview, mountFloatingButton, mountChat, unmount };

// IIFE mode in Vite sets `window.CalebasseChatbot = (the exported object)`.
export { mountOverview, mountFloatingButton, mountChat, unmount };

// Also attach explicitly for environments where the IIFE wrapper might
// not unify exports the way we expect (safety net).
if (typeof window !== 'undefined') {
  (window as unknown as { CalebasseChatbot?: CalebasseChatbotGlobal }).CalebasseChatbot = api;
}
